/**
 * What each notification rule needs before it can do anything (2026-10-30, Hugo Andrade: "if a notification
 * needs a data collection that is off, is it greyed out and unavailable to turn on?").
 *
 * One map for the backend (which refuses to turn on a rule that cannot work: routes/customer-notifications.mjs
 * and routes/watch-settings.mjs) and the Rules page (which greys it out, or shows it paused, with the same words).
 *
 *   Daily rules: the collector their event diffs (shared/notifications.mjs `dataSource`), plus the Microsoft
 *   Entra ID license that collector needs (shared/collectors.mjs SOURCE_LICENSES).
 *   Live watch rules: Watch reads the Microsoft Entra audit log itself, so no rule depends on a daily collector
 *   (the daily snapshots only speed up recipient lookups, with Graph as the fallback). A rule read from
 *   Microsoft Entra ID Protection (source 'risk') needs P2; one read from the sign-in log (source 'sign-in') P1.
 *
 * Nothing here ever turns a data source on: the customer decides in Data collection.
 *
 * Pure, no I/O, no Node-only import: the portal frontend imports it too.
 */
import { NOTIFICATION_EVENTS } from './notifications.mjs';
import { COLLECTOR_CATEGORIES, LICENSE_LABELS, SOURCE_LICENSES } from './collectors.mjs';
import { CATALOG } from './watch-catalog.mjs';

export const dailyRuleKey = (eventId) => `daily:${eventId}`;
export const liveRuleKey = (catalogId) => `live:${catalogId}`;

const LIVE_SOURCE_LICENSE = Object.freeze({ risk: 'P2', 'sign-in': 'P1' });

export const RULE_DEPENDENCIES = Object.freeze(Object.fromEntries([
  ...NOTIFICATION_EVENTS.map((e) => [dailyRuleKey(e.id), Object.freeze({ source: e.dataSource, license: SOURCE_LICENSES[e.dataSource] ?? null })]),
  ...CATALOG.filter((e) => e.detection === 'live').map((e) => [liveRuleKey(e.id), Object.freeze({ source: null, license: LIVE_SOURCE_LICENSE[e.source] ?? null })]),
]));

const SOURCE_LABELS = Object.freeze(Object.fromEntries(COLLECTOR_CATEGORIES.flatMap((c) => c.collectors).map((c) => [c.id, c.label])));
export const sourceLabel = (id) => SOURCE_LABELS[id] ?? id;

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * The state the map is checked against, from the customer config row (enabledCollectors, licenseDisabled,
 * tenantLicenses) and, for the live risk read, Watch's own health report. A license level is true, false or
 * null (unknown). The daily license check wins when it has run; Watch reporting its risk read as 'unlicensed'
 * counts only while the level is unknown. Unknown never blocks a rule.
 */
export function dependencyContext({ config, watchHealth } = {}) {
  const enabled = new Set(Array.isArray(config?.enabledCollectors) ? config.enabledCollectors : []);
  const licenseDisabled = {};
  if (isObject(config?.licenseDisabled)) {
    for (const [name, v] of Object.entries(config.licenseDisabled)) if (isObject(v) && Object.hasOwn(LICENSE_LABELS, v.license)) licenseDisabled[name] = v.license;
  }
  const known = isObject(config?.tenantLicenses);
  const level = (k) => (known && typeof config.tenantLicenses[k] === 'boolean' ? config.tenantLicenses[k] : null);
  const licenses = { P1: level('P1'), P2: level('P2') };
  if (licenses.P2 === null && watchHealth?.riskRead?.status === 'unlicensed') licenses.P2 = false;
  return { enabled, licenseDisabled, licenses };
}

/**
 * { available: true } or { available: false, cause, source, license, reason, paused, link }.
 * `reason` is why the rule cannot be turned on; `paused` is shown on a rule that is on but cannot run.
 * `link` is 'data-collection' when the customer can fix it there, else null.
 */
export function ruleDependency(ruleKey, ctx) {
  const dep = Object.hasOwn(RULE_DEPENDENCIES, ruleKey) ? RULE_DEPENDENCIES[ruleKey] : null;
  if (!dep) return { available: true };
  const c = ctx ?? dependencyContext({});
  if (dep.source && !c.enabled.has(dep.source)) {
    const label = sourceLabel(dep.source);
    const lic = c.licenseDisabled[dep.source];
    if (lic) {
      return {
        available: false, cause: 'source-unlicensed', source: dep.source, license: lic, link: 'data-collection',
        reason: `${label} is off because your tenant has no ${LICENSE_LABELS[lic]}. Turn on ${label} in Data collection first.`,
        paused: `Paused: ${label} is off (requires ${LICENSE_LABELS[lic]}).`,
      };
    }
    return {
      available: false, cause: 'source-off', source: dep.source, license: null, link: 'data-collection',
      reason: `Turn on ${label} in Data collection first.`,
      paused: `Paused: ${label} is off.`,
    };
  }
  if (!dep.source && dep.license && c.licenses[dep.license] === false) {
    return {
      available: false, cause: 'unlicensed', source: null, license: dep.license, link: null,
      reason: `Requires ${LICENSE_LABELS[dep.license]}, which your tenant does not have.`,
      paused: `Paused: requires ${LICENSE_LABELS[dep.license]}.`,
    };
  }
  return { available: true };
}

/**
 * The refusal for a save that would switch an unavailable rule from off to on, or null. Turning a paused
 * rule off, or editing one that is already on, is never refused: the customer must always be able to stop it.
 */
export function enablingRefusal(ruleKey, { wasOn, nextOn }, ctx) {
  if (nextOn !== true || wasOn === true) return null;
  const d = ruleDependency(ruleKey, ctx);
  return d.available ? null : d.reason;
}
